import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Link,
  Avatar,
  Fade,
  Divider
} from '@mui/material';
import { 
  Download, 
  Trash2, 
  ExternalLink, 
  FileText, 
  Link as LinkIcon,
  Layers
} from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  dark: "#22404D",
  azur: "#28708B",
  red: "#F65F4E",
  pearl: "#EEF3F4"
};

const ListadoArchivosComunicacion = ({ pedidoId, tareaId, actualizar }) => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const ObtenerArchivos = async (id, idTarea) => {
    try {
      const response = await fetch(`${apiUrl}/Archivo/getArchivosDelPedidoPorTarea?idPedido=${id}&idTarea=${idTarea}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const files = await response.json();
      setUploadedFiles(files);
    } catch (error) {
      toast.error('Error al obtener archivos de la tarea');
    }
  };

  useEffect(() => {
    if (pedidoId && tareaId) {
      ObtenerArchivos(pedidoId, tareaId);
    }
  }, [actualizar, tareaId, pedidoId, apiUrl]);

  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(`https://pideapi2025.azurewebsites.net/api/Archivo/descargar/${fileName}`);
      if (!response.ok) throw new Error("Error en descarga");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleDelete = async (nombreArchivo, urlAzure) => {
    try {
      const response = await fetch(`https://pideapi2025.azurewebsites.net/api/Archivo/BorrarArchivoComunicacion?nombreArchivo=${nombreArchivo}&pedidoId=${pedidoId}&urlAzure=${urlAzure}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error("Error al eliminar");
      
      ObtenerArchivos(pedidoId, tareaId);
      toast.success('Archivo eliminado correctamente');
    } catch (error) {
      toast.error('No se pudo eliminar el archivo');
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: PALETTE.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Layers size={18} color={PALETTE.teal} /> Entregables de esta tarea
      </Typography>

      {uploadedFiles.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa', borderRadius: '20px', border: '1px dashed #ccc' }}>
          <Typography variant="body2" sx={{ color: '#999' }}>
            No hay archivos adjuntos todavía.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {uploadedFiles.map((file, index) => (
            <Fade in={true} key={index}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 1.5, 
                  pl: 2,
                  borderRadius: '16px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  border: '1px solid #f0f0f0',
                  transition: '0.2s',
                  '&:hover': { borderColor: PALETTE.sky, bgcolor: '#fcfcfc' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, minWidth: 0 }}>
                  <Avatar sx={{ 
                    bgcolor: file.link ? PALETTE.ice : PALETTE.pearl, 
                    color: file.link ? PALETTE.azur : PALETTE.teal,
                    width: 32, height: 32
                  }}>
                    {file.link ? <LinkIcon size={16} /> : <FileText size={16} />}
                  </Avatar>
                  
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 700, 
                      color: PALETTE.dark,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '180px'
                    }}>
                      {file.nombreArchivo}
                    </Typography>
                    
                    {file.link && (
                      <Link 
                        href={file.link} 
                        target="_blank" 
                        sx={{ 
                          fontSize: '0.7rem', 
                          color: PALETTE.aqua, 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          textDecoration: 'none'
                        }}
                      >
                        Ver enlace <ExternalLink size={10} />
                      </Link>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Descargar">
                    <IconButton 
                      onClick={() => handleDownload(file.nombreArchivo)}
                      size="small"
                      sx={{ color: PALETTE.teal }}
                    >
                      <Download size={18} />
                    </IconButton>
                  </Tooltip>

                  {localStorage.getItem('rol') === 'comunicacion' && (
                    <Tooltip title="Eliminar">
                      <IconButton 
                        onClick={() => handleDelete(file.nombreArchivo, file.urlAzure)}
                        size="small"
                        sx={{ color: PALETTE.red, ml: 0.5 }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            </Fade>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ListadoArchivosComunicacion;